import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../lib/api';

// Send cookies with every cross-call so the httpOnly auth cookie reaches the API.
axios.defaults.withCredentials = true;

// Read a non-HttpOnly cookie value by name (used to echo the CSRF token back
// to the server in the X-CSRF-Token header — the double-submit pattern).
const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionEnded, setSessionEnded] = useState(false);

  // `authHeader` now only carries the CSRF token. The auth cookie is sent
  // automatically by the browser because of `withCredentials: true`.
  // Name retained for backwards compatibility with all existing callers.
  const authHeader = useCallback(() => {
    const csrf = readCookie('csrf_token');
    return csrf ? { 'X-CSRF-Token': csrf } : {};
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch (err) {
      // No valid session — that's expected on first visit / after logout.
      if (err?.response?.status !== 401) {
        console.warn('Session check failed:', err?.message || err);
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe().finally(() => setLoading(false));
  }, [fetchMe]);

  const login = async (email, password) => {
    setSessionEnded(false);
    await axios.post(`${API}/auth/login`, { email, password });
    // Cookies are now set by the server; pull the canonical user record
    // (including password_changed_at + role) from /me. Return the freshly-
    // fetched user so the caller can branch on role (e.g. staff → /staff).
    const me = await fetchMe();
    return me;
  };

  const register = async ({ email, password, name, phone }) => {
    await axios.post(
      `${API}/auth/register`,
      { email, password, name, phone },
    );
    return await fetchMe();
  };

  // Step 1 of OTP signup: collect email/name/phone → server emails a code.
  const otpStart = async ({ email, name, phone }) => {
    const { data } = await axios.post(`${API}/auth/register/start`, { email, name, phone });
    return data;
  };

  // Step 2: submit the OTP → server creates the user, sets cookies, we
  // then refresh /me so AuthContext is populated.
  const otpVerify = async ({ email, otp }) => {
    await axios.post(`${API}/auth/register/verify`, { email, otp });
    return await fetchMe();
  };

  // Passwordless re-login (returning customer). Same two-step pattern as
  // signup but uses `purpose=login` on the backend.
  const loginOtpStart = async ({ email }) => {
    const { data } = await axios.post(`${API}/auth/login/start`, { email });
    return data;
  };

  const loginOtpVerify = async ({ email, otp }) => {
    await axios.post(`${API}/auth/login/verify`, { email, otp });
    return await fetchMe();
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: authHeader() });
    } catch (err) {
      // Logout should proceed even if the server call fails; log for diagnostics.
      console.warn('Logout request failed (proceeding with local cleanup):', err?.message || err);
    }
    setUser(null);
    // Marks a deliberate sign-out so ProtectedRoute stops remembering the
    // route we just left — otherwise the next person to sign in on this tab
    // inherits the previous user's page.
    setSessionEnded(true);
  };

  return (
    <AuthContext.Provider value={{ user, loading, sessionEnded, login, register, otpStart, otpVerify, loginOtpStart, loginOtpVerify, logout, authHeader }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
